import { toast } from "@hortis/ui/sonner";
import type { Dispatch, SetStateAction } from "react";
import { useCallback } from "react";
import type { FileRejection } from "react-dropzone";
import { useDropzone } from "react-dropzone";
import type {
  LocalImage,
  StagedImage,
} from "src/features/records/material/components/material-images-tab/types";
import { processDropzoneErrors } from "src/features/records/material/components/material-images-tab/utils";
import { useAccessTokenBasic } from "src/utils/use-access-token";
import * as uuid from "uuid";

export type UseImageSelectArgs = {
  setImage:
    | Dispatch<SetStateAction<StagedImage>>
    | ((image: StagedImage) => void);
  onFileRejection?: (errors: Array<string>) => void;
  uploadFile: (args: { id: string; file: File; accessToken: string }) => void;
  maxSize?: number;
  maxFiles?: number;
};

export const useImageSelect = ({
  setImage,
  onFileRejection,
  uploadFile,
  maxSize = 1_000_000,
  maxFiles = 1,
}: UseImageSelectArgs) => {
  const accessToken = useAccessTokenBasic();

  const handleFileSelection = useCallback(
    (acceptedFiles: Array<File>) => {
      for (const acceptedFile of acceptedFiles) {
        if (accessToken == null) {
          toast.error("No access token found");
          return;
        }
        const id = uuid.v4();
        const localImage: LocalImage = {
          localId: id,
          id,
          file: acceptedFile,
          thumb: URL.createObjectURL(acceptedFile),
          progress: 0,
          complete: false,
        };
        setImage(localImage);
        uploadFile({ id, file: acceptedFile, accessToken });
      }
    },
    [accessToken, setImage, uploadFile],
  );

  const onDropRejected = useCallback(
    (errors: Array<FileRejection>) => {
      onFileRejection == null
        ? undefined
        : onFileRejection(processDropzoneErrors(errors, maxFiles, maxSize));
    },
    [onFileRejection, maxFiles, maxSize],
  );

  const dropzoneArgs = useDropzone({
    maxFiles,
    onDropRejected,
    onDrop: handleFileSelection,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpeg"],
      "image/jpg": [".jpg"],
      "image/gif": [".gif"],
      "image/webp": [".webp"],
    },
    maxSize,
  });

  return {
    ...dropzoneArgs,
    errors: processDropzoneErrors(
      dropzoneArgs.fileRejections,
      maxFiles,
      maxSize,
    ),
  };
};
